import React, { useMemo } from "react";
import { ShieldAlert } from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";
import { ProcessedScanResult } from "../SpaceScanner.types";
import {
  analyzeConstraintTradeoffs,
  ConstraintPriority,
  ConstraintTradeoff
} from "../../../../../services/ai/SpaceOptimiserAI";

interface ConstraintTradeoffsProps {
  results: ProcessedScanResult[];
}

const PRIORITY_COLOURS: Record<ConstraintPriority, string> = {
  high: "hsl(var(--destructive))",
  medium: "hsl(var(--warning))",
  low: "hsl(var(--muted-foreground))"
};

const PRIORITY_LABELS: Record<ConstraintPriority, string> = {
  high: "High",
  medium: "Medium",
  low: "Low"
};

const CONSTRAINT_LABELS: Record<string, string> = {
  vip: "VIP",
  dietary: "Dietary",
  accessibility: "Accessibility"
};

const TradeoffItem = ({
  tradeoff
}: {
  tradeoff: ConstraintTradeoff;
}): React.ReactElement => {
  const colour = PRIORITY_COLOURS[tradeoff.priority];

  return (
    <div className="rounded-md bg-card p-2.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        {tradeoff.constraints.map((constraint) => (
          <span
            key={constraint}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
            style={{ backgroundColor: colour }}
          >
            {CONSTRAINT_LABELS[constraint] ?? constraint}
          </span>
        ))}
        <span
          className="ml-auto text-[11px] font-medium"
          style={{ color: colour }}
        >
          {PRIORITY_LABELS[tradeoff.priority]} priority
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{tradeoff.explanation}</p>
    </div>
  );
};

export const ConstraintTradeoffs = ({
  results
}: ConstraintTradeoffsProps): React.ReactElement | null => {
  const tradeoffs = useMemo(
    () => analyzeConstraintTradeoffs(results),
    [results]
  );

  if (tradeoffs.length === 0) {
    return (
      <section
        className="my-3 rounded-lg border border-border p-3"
        aria-label="Constraint trade-offs"
      >
        <div className="flex items-center gap-2">
          <ShieldAlert
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-foreground">
            Constraint Trade-offs
          </span>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          No constraint trade-offs detected in this scan.
        </p>
      </section>
    );
  }

  return (
    <CollapsibleSection
      icon={
        <ShieldAlert
          className="size-4 flex-shrink-0 text-[hsl(var(--warning))]"
          aria-hidden="true"
        />
      }
      title={`${tradeoffs.length} Constraint Trade-off${tradeoffs.length !== 1 ? "s" : ""} Detected`}
      ariaLabel="Constraint trade-offs"
    >
      {tradeoffs.map((tradeoff) => (
        <TradeoffItem key={tradeoff.bookingId} tradeoff={tradeoff} />
      ))}
    </CollapsibleSection>
  );
};
